import {useEffect, useRef} from 'react'
import {ImgCaption as ImgCaptionModel} from '../../models/editor/imgCaption'
import {useAppDispatch} from '../../stores/configureStore'
import {setAttribute} from '../../stores/editor/editor.reducer'
import StyleSheet from '../../utils/helpers/styleSheet'

interface ImgCaptionProps {
  img: ImgCaptionModel
  onMouseDown?: React.MouseEventHandler | undefined
  onMouseUp?: React.MouseEventHandler | undefined
  onTouchStart: (this: any, e: TouchEvent) => void
  onTouchEnd: (this: any, e: TouchEvent) => void
}

export default function ImgCaption({
  img,
  onMouseDown,
  onMouseUp,
  onTouchStart,
  onTouchEnd,
}: ImgCaptionProps) {
  const dispatch = useAppDispatch()
  const mouseEventHandlers = {onMouseDown, onMouseUp}
  useEffect(() => {
    const calculateSvgWidth = async () => {
      const group = document.getElementById(
        `svgGroup_${img.id}`,
      ) as SVGGElement | null
      if (group) {
        const bBox = group.getBBox()
        dispatch(setAttribute({id: img.id, data: {width: bBox.width + 20}}))
        dispatch(setAttribute({id: img.id, data: {height: bBox.height + 20}}))
      }
    }
    setTimeout(calculateSvgWidth, 100)
    setTimeout(calculateSvgWidth, 1500)
  }, [
    dispatch,
    img.id,
    img.caption1,
    img.caption2,
    img.bold,
    img.italic,
    img.fontFamily,
    img.curvatureLevel,
    img.letterSpacing1,
    img.letterSpacing2,
    img.color,
  ])

  const MARGIN = 10
  const RADIUS1 = 550 - Math.abs(img.curvatureLevel) * 25
  const RADIUS2 = RADIUS1 - 1.8 * MARGIN
  const IMGSIZE = 2 * (MARGIN + RADIUS1)

  const styles: StyleSheet = {
    linearStyle: {
      pointerEvents: 'none',
    },
    curveStyle: {
      pointerEvents: 'none',
      transform:
        img.curvatureLevel < 0
          ? `translateY(-${RADIUS2}px)`
          : `translateY(${RADIUS2}px)`,
    },
  }

  const firstRowRef = useRef<SVGTextElement>(null)
  const secondRowRef = useRef<SVGTextElement>(null)

  useEffect(() => {
    if (firstRowRef.current && secondRowRef.current) {
      const firstRowRefCurrent = firstRowRef.current
      const secondRowRefCurrent = secondRowRef.current

      firstRowRefCurrent.addEventListener('touchstart', onTouchStart, {
        passive: false,
      })
      secondRowRefCurrent.addEventListener('touchstart', onTouchStart, {
        passive: false,
      })
      firstRowRefCurrent.addEventListener('touchend', onTouchEnd, {
        passive: false,
      })
      secondRowRefCurrent.addEventListener('touchend', onTouchEnd, {
        passive: false,
      })
      return () => {
        firstRowRefCurrent.removeEventListener('touchstart', onTouchStart)
        secondRowRefCurrent.removeEventListener('touchstart', onTouchStart)
        firstRowRefCurrent.removeEventListener('touchend', onTouchEnd)
        secondRowRefCurrent.removeEventListener('touchend', onTouchEnd)
      }
    }
  }, [onTouchStart, onTouchEnd])

  return (
    <svg
      width={img.curvatureLevel === 0 ? img.width : IMGSIZE}
      height={img.curvatureLevel === 0 ? img.height : IMGSIZE}
      viewBox={`0 0 ${img.curvatureLevel === 0 ? img.width : IMGSIZE} ${
        img.curvatureLevel === 0 ? img.height : IMGSIZE
      }`}
      style={img.curvatureLevel === 0 ? styles.linearStyle : styles.curveStyle}>
      <g x="0" y="0" id={`svgGroup_${img.id}`}>
        {img.curvatureLevel > 0 && (
          <>
            <path
              id={`path1_${img.id}`}
              fill="none"
              stroke="none"
              d={`M ${IMGSIZE / 2}, ${
                MARGIN + RADIUS1 + RADIUS1
              } a ${RADIUS1},${RADIUS1} 0 1,1 0,-${
                2 * RADIUS1
              } a ${RADIUS1},${RADIUS1} 0 1,1 0,${2 * RADIUS1}`}
            />
            <path
              id={`path2_${img.id}`}
              fill="none"
              stroke="none"
              d={`M ${IMGSIZE / 2}, ${
                MARGIN + RADIUS1 + RADIUS2
              } a ${RADIUS2},${RADIUS2} 0 1,1 0,-${
                2 * RADIUS2
              } a ${RADIUS2},${RADIUS2} 0 1,1 0,${2 * RADIUS2}`}
            />
          </>
        )}
        {img.curvatureLevel < 0 && (
          <>
            <path
              id={`path2_${img.id}`}
              fill="none"
              stroke="none"
              d={`M ${IMGSIZE / 2}, ${
                MARGIN + RADIUS1 - RADIUS1
              } a ${RADIUS1},${RADIUS1} 0 1,0 0,${
                2 * RADIUS1
              } a ${RADIUS1},${RADIUS1} 0 1,0 0,-${2 * RADIUS1}`}
            />
            <path
              id={`path1_${img.id}`}
              fill="none"
              stroke="none"
              d={`M ${IMGSIZE / 2}, ${
                MARGIN + RADIUS1 - RADIUS2
              } a ${RADIUS2},${RADIUS2} 0 1,0 0,${
                2 * RADIUS2
              } a ${RADIUS2},${RADIUS2} 0 1,0 0,-${2 * RADIUS2}`}
            />
          </>
        )}
        <text
          ref={firstRowRef}
          x={img.curvatureLevel === 0 ? '50%' : '0'}
          y="15"
          dominantBaseline="middle"
          textAnchor="middle"
          fontFamily={img.fontFamily}
          fill={img.color}
          fontWeight={img.bold ? 'bold' : 'normal'}
          textDecoration={img.underline ? 'underline' : 'none'}
          fontStyle={img.italic ? 'italic' : 'normal'}
          letterSpacing={img.letterSpacing1}
          style={{
            cursor: !img.locked && !img.inert ? 'pointer' : 'default',
            pointerEvents: !img.locked && !img.inert ? 'initial' : 'none',
          }}
          {...mouseEventHandlers}>
          {img.curvatureLevel === 0 ? (
            img.caption1
          ) : (
            <textPath href={`#path1_${img.id}`} startOffset="50%">
              {img.caption1}
            </textPath>
          )}
        </text>
        <text
          ref={secondRowRef}
          x={img.curvatureLevel === 0 ? '50%' : '0'}
          y="35"
          dominantBaseline="middle"
          textAnchor="middle"
          fontFamily={img.fontFamily}
          fill={img.color}
          fontWeight={img.bold ? 'bold' : 'normal'}
          textDecoration={img.underline ? 'underline' : 'none'}
          fontStyle={img.italic ? 'italic' : 'normal'}
          letterSpacing={img.letterSpacing2}
          style={{
            cursor: !img.locked && !img.inert ? 'pointer' : 'default',
            pointerEvents: !img.locked && !img.inert ? 'initial' : 'none',
          }}
          {...mouseEventHandlers}>
          {img.curvatureLevel === 0 ? (
            img.caption2
          ) : (
            <textPath href={`#path2_${img.id}`} startOffset="50%">
              {img.caption2}
            </textPath>
          )}
        </text>
      </g>
    </svg>
  )
}
